import { Calendar } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { DateFilterType } from 'src/pages/performance/tabs/overview-tab';
import { getMonthBounds } from 'src/pages/performance/utils/date-utils';
import { cn } from 'src/lib/utils';

interface MartReportsDateRangePickerProps {
  dateFilter: DateFilterType;
  startDate: string;
  endDate: string;
  onChange: (filter: DateFilterType, startDate: string, endDate: string) => void;
}

const QUICK_RANGES: { key: DateFilterType; label: string }[] = [
  { key: 'last_week', label: 'Last week' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_month', label: 'This month' },
];

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateLocal = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const resolvePresetRange = (preset: DateFilterType): { start: Date; end: Date } => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  switch (preset) {
    case 'last_week': {
      const start = new Date(now);
      start.setDate(now.getDate() - 7);
      return { start, end: new Date(now) };
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start, end };
    }
    case 'this_month':
      return { start: getMonthBounds(now).first, end: yesterday };
    default:
      return { start: now, end: now };
  }
};

const buildCalendarCells = (viewDate: Date) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
};

export function MartReportsDateRangePicker({
  dateFilter,
  startDate,
  endDate,
  onChange,
}: MartReportsDateRangePickerProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => parseDateLocal(startDate));
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);

  const selectedStart = useMemo(() => parseDateLocal(startDate), [startDate]);
  const selectedEnd = useMemo(() => parseDateLocal(endDate), [endDate]);
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    if (!calendarOpen) {
      setDraftStart(null);
      setDraftEnd(null);
    }
  }, [calendarOpen]);

  const handlePresetClick = (preset: DateFilterType) => {
    const { start, end } = resolvePresetRange(preset);
    onChange(preset, formatDateLocal(start), formatDateLocal(end));
    setCalendarOpen(false);
  };

  const handleDayClick = (day: Date) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(day);
      setDraftEnd(null);
      return;
    }

    const start = draftStart <= day ? draftStart : day;
    const end = draftStart <= day ? day : draftStart;
    setDraftStart(start);
    setDraftEnd(end);
    onChange('custom', formatDateLocal(start), formatDateLocal(end));
    setCalendarOpen(false);
  };

  const rangeStart = draftStart ?? selectedStart;
  const rangeEnd = draftEnd ?? (draftStart ? null : selectedEnd);

  const orderedRange = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    return rangeStart <= rangeEnd
      ? { start: rangeStart, end: rangeEnd }
      : { start: rangeEnd, end: rangeStart };
  }, [rangeStart, rangeEnd]);

  const cells = buildCalendarCells(viewDate);

  return (
    <div className="inline-flex items-center gap-1 rounded-full border-[1.5px] border-[#D9DBE3] bg-[#F4F5F8] p-1">
      {QUICK_RANGES.map((range) => (
        <button
          key={range.key}
          type="button"
          onClick={() => handlePresetClick(range.key)}
          className={cn(
            'rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors',
            dateFilter === range.key
              ? 'bg-[#2563EB] text-white'
              : 'text-[#5E6278] hover:text-[#0D0F1A]',
          )}
        >
          {range.label}
        </button>
      ))}

      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Custom date range"
            className={cn(
              'flex h-[30px] w-[30px] items-center justify-center rounded-full transition-colors',
              dateFilter === 'custom'
                ? 'bg-[#2563EB] text-white'
                : 'text-[#5E6278] hover:bg-white hover:text-[#0D0F1A]',
            )}
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto border-[#E8E9ED] p-0 shadow-lg">
          <div className="w-[260px] rounded-xl bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                className="flex h-7 w-7 items-center justify-center rounded-md border border-[#D9DBE3] text-[#5E6278] hover:bg-[#F4F5F8]"
                onClick={() =>
                  setViewDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))
                }
              >
                ‹
              </button>
              <span className="text-[13.5px] font-bold text-[#0D0F1A]">
                {viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </span>
              <button
                type="button"
                aria-label="Next month"
                className="flex h-7 w-7 items-center justify-center rounded-md border border-[#D9DBE3] text-[#5E6278] hover:bg-[#F4F5F8]"
                onClick={() =>
                  setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))
                }
              >
                ›
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {DAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="py-1 text-center text-[10px] font-bold tracking-[0.3px] text-[#8A8EA0]"
                >
                  {label}
                </div>
              ))}
              {cells.map((day) => {
                const inCurrentMonth = day.getMonth() === viewDate.getMonth();
                const isToday = isSameDay(day, today);
                const isStart =
                  (draftStart && isSameDay(day, draftStart)) ||
                  (orderedRange && isSameDay(day, orderedRange.start));
                const isEnd =
                  (draftEnd && isSameDay(day, draftEnd)) ||
                  (orderedRange && isSameDay(day, orderedRange.end));
                const inRange =
                  orderedRange &&
                  day >= orderedRange.start &&
                  day <= orderedRange.end &&
                  !isStart &&
                  !isEnd;

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      'rounded-md py-1.5 text-center text-[12.5px] font-medium transition-colors',
                      !inCurrentMonth && 'text-[#B8BBCA]',
                      inCurrentMonth && 'text-[#0D0F1A] hover:bg-[#F4F5F8]',
                      inRange && 'bg-[#EFF6FF] text-[#2563EB]',
                      (isStart || isEnd) && 'bg-[#2563EB] text-white',
                      isToday && !isStart && !isEnd && 'font-bold',
                    )}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <p className="mt-3 text-center text-[11px] text-[#8A8EA0]">
              {draftStart && !draftEnd
                ? 'Select end date'
                : `${startDate} → ${endDate}`}
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
